import { useEffect, useMemo, useState } from "react";

import {
  getAnalyticsWidgetData,
  getReportWidgetData,
  getTaskStatus,
  newTask,
} from "@/api/endpoints/task";
import { TASK_STATUS } from "@/constants";
import { useI18n } from "@/contexts/i18nContext";
import { cx } from "@/utils";
import {
  ANALYTICS_WIDGETS_URL_MAP,
  METRIC_DISPLAY_MAP,
  METRIC_WIDGET_MAP,
  REPORT_WIDGETS_URL_MAP,
} from "@/views/reports/widgets/settings";

import { poll } from "./helpers";

const Widget = ({
  metric,
  size,
  endDate,
  startDate,
  dontSize,
  uuid,
  filters,
  searchId,
  ...props
}) => {
  const [searchData, setSearchData] = useState(null);
  const [error, setError] = useState(false);
  const [taskId, setTaskId] = useState(null);

  const analyticsWidgetUrl = ANALYTICS_WIDGETS_URL_MAP[metric];
  const reportWidgetUrl = REPORT_WIDGETS_URL_MAP[metric];

  useEffect(() => {
    let outdated = false;
    if (taskId) {
      poll({
        fn: () => getTaskStatus("widget_stats", taskId),
        validate: ({ data }) => data.task_status === TASK_STATUS.SUCCESS,
        interval: 2000,
        maxAttempts: 150,
        checkError: ({ data }) =>
          data.task_status === TASK_STATUS.FAILURE || outdated,
      })
        .then(({ data }) => {
          if (outdated === false) {
            data && setSearchData(data.data);
          }
        })
        .catch((error) => {
          if (outdated === false) {
            setError(true);
            throw error;
          }
        });
    }
    return () => {
      outdated = true;
    };
  }, [taskId]);

  const { language, currency } = useI18n();

  useEffect(() => {
    if (!uuid) {
      return;
    }
    setSearchData(null);
    if (startDate && endDate) {
      // Some strings are calculated and returned from server side, which
      // means we need to send through the current locale parameter
      if (analyticsWidgetUrl && searchId) {
        // <- WHEN USING THE SYNC ANALYTICS ENDPOINTS
        getAnalyticsWidgetData(analyticsWidgetUrl, metric, searchId, {
          end_date: endDate,
          start_date: startDate,
          locale: language,
          currency,
          ...filters,
        }).then(({ data, ok }) => {
          if (!ok) {
            setError(true);
          } else {
            setSearchData([data]);
          }
        });
      } else if (reportWidgetUrl && uuid) {
        // <- WHEN USING THE SYNC REPORT ENDPOINTS
        getReportWidgetData(reportWidgetUrl, metric, uuid, {
          locale: language,
          currency,
          ...filters,
          end_date: endDate,
          start_date: startDate,
        }).then(({ data, ok }) => {
          if (!ok) {
            setError(true);
          } else {
            setSearchData(data);
          }
        });
      } else {
        // <- WHEN USING THE ASYNC ENDPOINT
        newTask("widget_stats", {
          widget_uuid: uuid,
          end_date: endDate,
          start_date: startDate,
          locale: language,
          currency,
          filters,
        })
          .then(({ data }) => {
            setTaskId(data.task_id);
          })
          .catch(() => {
            setTaskId(null);
          });
      }
    }
  }, [
    currency,
    filters,
    endDate,
    startDate,
    uuid,
    language,
    metric,
    searchId,
    analyticsWidgetUrl,
    reportWidgetUrl,
  ]);

  const Component = useMemo(() => METRIC_WIDGET_MAP[metric], [metric]);
  if (!Component) return null;

  const widget = (
    <Component
      metric={metric}
      endDate={endDate}
      startDate={startDate}
      uuid={uuid}
      filters={filters}
      key={props.uuid}
      data={searchData}
      error={error}
      size={size}
      {...props}
      // This recasing is unnecessary  - remove whenver possible
      chartType={props.chart_type}
      isCompact={props.is_compact}
      metricDisplay={METRIC_DISPLAY_MAP[metric]}
    />
  );

  const colSpanClass = (size) => {
    switch (size) {
      case 1:
        return "md:col-span-1";
      case 2:
        return "md:col-span-2";
      case 3:
        return "md:col-span-3";
      case 4:
        return "md:col-span-4";
      case 5:
        return "md:col-span-5";
      case 6:
        return "md:col-span-6";
      case 7:
        return "md:col-span-7";
      case 8:
        return "md:col-span-8";
      case 9:
        return "md:col-span-9";
      case 10:
        return "md:col-span-10";
      case 11:
        return "md:col-span-11";
      case 12:
        return "md:col-span-12";
      default:
        return "";
    }
  };

  // Make it possible to opt out from the sizing if we don't want it
  return dontSize ? (
    widget
  ) : (
    <div className={cx("pt-8 p-2 col-span-12", colSpanClass(size))}>
      {widget}
    </div>
  );
};

export { Widget };
